import { InjectionToken, Injectable, afterEveryRender, computed, signal } from '@angular/core';

/**
 * Cuántas marcas del modo soporte SE VEN (Historia 9.1, 4.º criterio).
 *
 * La marca vivía SÓLO en la ranura `aviso` del `MainLayout`, y dos salidas de la consola quedan
 * fuera de él: «Design Kit» (`/sistema-de-diseno`, colgada de la raíz porque tiene que andar sin
 * backend) y cualquier URL inexistente (`**` → `/sign-in`). Con una sesión de soporte abierta,
 * ahí no se veía en qué cuenta se estaba operando ni había cómo salir.
 *
 * La raíz (`App`) dibuja una marca de RESPALDO cuando no se ve ninguna: así la invariante no
 * depende de que cada layout —el de hoy o uno que se agregue mañana— se acuerde de ponerla. Hay
 * exactamente una a la vista: la del layout cuando se ve, la de la raíz cuando no.
 *
 * **Cuenta las que se ven, no las que existen.** Contaba INSTANCIAS, y la cuarta refutación
 * (2026-09-24) mostró por qué no alcanza: el shell proyecta `[aviso]` dentro del `@else` de su
 * pantalla de «cierre sin confirmar», y Angular instancia igual el contenido proyectado aunque
 * la rama no se dibuje. Con un 503 al cerrar sesión, la banda del layout existía —y se había
 * registrado— fuera del documento, la raíz no dibujaba la suya, y la sesión de soporte quedaba
 * abierta sin ninguna marca en pantalla. Lo mismo pasaría con una ranura de alto cero que recorta
 * (`h-0 overflow-clip`), o con cualquier layout que la esconda.
 *
 * Por eso cada banda registra su ELEMENTO, y después de cada render se vuelve a preguntar cuáles
 * están a la vista (`estaALaVista`). El render es el único momento en que el DOM cambia por la
 * consola, así que ese recuento no se queda viejo.
 */
@Injectable({ providedIn: 'root' })
export class MarcasDeSoporteMontadas {
  private readonly montadas = new Set<() => Element | null>();
  private readonly aLaVista = signal(0);

  /** Si alguna marca montada por un layout se ve. */
  public readonly hayUna = computed(() => this.aLaVista() > 0);

  constructor() {
    afterEveryRender({ read: () => this.revisar() });
  }

  /**
   * Registra una marca. `elemento` devuelve lo que se dibuja de ella (el bloque que dice «Modo
   * soporte»), o `null` si todavía no dibujó nada. Devuelve cómo soltarla al destruirse.
   */
  public montar(elemento: () => Element | null): () => void {
    this.montadas.add(elemento);
    return () => {
      this.montadas.delete(elemento);
      this.revisar();
    };
  }

  /** Vuelve a contar las marcas que se ven. */
  public revisar(): void {
    let visibles = 0;
    for (const elemento of this.montadas) {
      const el = elemento();
      if (el !== null && estaALaVista(el)) visibles++;
    }
    if (visibles !== this.aLaVista()) this.aLaVista.set(visibles);
  }
}

/**
 * Si un elemento se ve: está en el documento, ni él ni un ancestro lo esconde, y —donde hay
 * motor de layout— ningún ancestro que recorta lo deja en cero.
 *
 * Sin layout (jsdom, en los tests) no hay cajas que medir: se mira lo que sí se puede saber sin
 * medir —que esté en el documento y lo que dicen los estilos calculados—. El recorte por una caja
 * de alto cero lo detectan los specs por el efecto de sus clases (`a-la-vista.ts` del gate), y en
 * el navegador lo detecta la medición de abajo.
 */
export function estaALaVista(el: Element): boolean {
  if (!el.isConnected) return false;
  const ventana = el.ownerDocument.defaultView;
  for (let n: Element | null = el; n !== null; n = n.parentElement) {
    if (n instanceof HTMLElement && n.hidden) return false;
    const estilo = ventana?.getComputedStyle(n);
    if (estilo === undefined) continue;
    if (estilo.display === 'none') return false;
    if (estilo.opacity === '0') return false;
    if (n !== el && estilo.contentVisibility === 'hidden') return false;
  }
  const propio = ventana?.getComputedStyle(el);
  if (propio?.visibility === 'hidden' || propio?.visibility === 'collapse') return false;

  // Con motor de layout: la caja propia, recortada por cada ancestro que no deja desbordar.
  if (typeof (el as Element & { checkVisibility?: unknown }).checkVisibility !== 'function') {
    return true;
  }
  if (!el.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return false;
  let { left, top, right, bottom } = el.getBoundingClientRect();
  for (let n = el.parentElement; n !== null; n = n.parentElement) {
    const estilo = ventana!.getComputedStyle(n);
    if (estilo.overflowX === 'visible' && estilo.overflowY === 'visible') continue;
    const caja = n.getBoundingClientRect();
    if (estilo.overflowX !== 'visible') {
      left = Math.max(left, caja.left);
      right = Math.min(right, caja.right);
    }
    if (estilo.overflowY !== 'visible') {
      top = Math.max(top, caja.top);
      bottom = Math.min(bottom, caja.bottom);
    }
    if (right - left < 1 || bottom - top < 1) return false;
  }
  return right - left >= 1 && bottom - top >= 1;
}

/**
 * Marca a la banda de RESPALDO de la raíz: la que no se registra, porque es la que se dibuja
 * cuando no se ve ninguna. Viaja por inyección y no por atributo porque la raíz la crea a mano,
 * con una importación dinámica: una banda creada así no tiene atributos estáticos.
 */
export const MARCA_DE_RESPALDO = new InjectionToken<boolean>('marca de soporte de respaldo');
